import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { QuizQuestion } from "./QuizQuestion";
import type { LessonStage, QuizData } from "../types";

interface StageQuizProps {
  stage: LessonStage;
  onComplete: (correct: number, total: number) => void;
}

const STAR_RATINGS: Record<number, { stars: number; labelKey: string; color: string }> = {
  0: { stars: 0, labelKey: "quiz_keep_practicing", color: "text-muted-foreground" },
  1: { stars: 1, labelKey: "quiz_good_start", color: "text-foreground" },
  2: { stars: 2, labelKey: "quiz_well_done", color: "text-foreground" },
  3: { stars: 3, labelKey: "quiz_perfect", color: "text-secondary" },
};

export const StageQuiz = ({ stage, onComplete }: StageQuizProps) => {
  const { t } = useTranslation();
  const questions = stage.quizStage?.questions ?? (stage.quiz ? [stage.quiz] : []);
  const total = questions.length;

  const [current, setCurrent] = useState(0);
  const [results, setResults] = useState<boolean[]>([]);
  const [showXp, setShowXp] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Store final results for the rating screen
  const [finalResults, setFinalResults] = useState<{ correct: number; total: number } | null>(null);

  // Auto-call onComplete after showing rating card
  useEffect(() => {
    if (!showRating || !finalResults) return;
    const timer = setTimeout(() => {
      onCompleteRef.current(finalResults.correct, finalResults.total);
    }, 2000);
    return () => clearTimeout(timer);
  }, [showRating, finalResults]);

  if (total === 0) return null;

  const handleAnswer = (_index: number, isCorrect: boolean) => {
    const newResults = [...results, isCorrect];
    setResults(newResults);

    // Show floating XP on correct
    if (isCorrect) {
      setShowXp(true);
      setTimeout(() => setShowXp(false), 1000);
    }

    // Auto-advance after 1.5s
    setTimeout(() => {
      if (newResults.length < total) {
        setCurrent((c) => c + 1);
      } else {
        const correctCount = newResults.filter(Boolean).length;
        setFinalResults({ correct: correctCount, total });
        setShowRating(true);
      }
    }, 1500);
  };

  const correctSoFar = results.filter(Boolean).length;

  // Streak: consecutive correct from the end of results
  const streak = (() => {
    let count = 0;
    for (let i = results.length - 1; i >= 0; i--) {
      if (results[i]) count++;
      else break;
    }
    return count;
  })();

  const q = questions[current];

  // Rating screen
  if (showRating && finalResults) {
    const rating = STAR_RATINGS[finalResults.correct] ?? STAR_RATINGS[0];
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[250px] gap-4"
        style={{ animation: "scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
      >
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={cn(
                "text-4xl transition-all",
                i < rating.stars ? "opacity-100" : "opacity-20 grayscale",
              )}
              style={
                i < rating.stars
                  ? {
                      animation: `starScaleIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.15}s both`,
                    }
                  : undefined
              }
            >
              &#11088;
            </span>
          ))}
        </div>
        <p className={cn("text-lg font-bold", rating.color)}>
          {t(rating.labelKey)}
        </p>
        <p className="text-sm text-muted-foreground">
          {finalResults.correct}/{finalResults.total}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Score bar */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {t("learn_question_progress", "Question {{current}}/{{total}}", {
            current: current + 1,
            total,
          })}
        </span>
        <div className="flex items-center gap-2">
          {/* Streak indicator */}
          {streak >= 2 && (
            <span
              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-bold"
              style={{ animation: "bounceIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)" }}
            >
              <span role="img" aria-label="fire">&#128293;</span> {t("quiz_streak", { count: streak })}
            </span>
          )}
          <span className="font-bold text-primary">
            {correctSoFar}/{results.length}
          </span>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex gap-1.5">
        {questions.map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-all duration-300",
              i < results.length
                ? results[i]
                  ? "bg-primary"
                  : "bg-destructive"
                : i === current
                  ? "bg-[hsl(var(--level-accent,45_100%_51%))]"
                  : "bg-white/10",
            )}
            style={
              i === current
                ? { boxShadow: "0 0 8px hsl(var(--level-accent, 45 100% 51%) / 0.5)" }
                : undefined
            }
          />
        ))}
      </div>

      {/* Floating +10 XP */}
      {showXp && (
        <div
          className="absolute top-0 right-0 text-primary font-bold text-lg pointer-events-none"
          style={{ animation: "floatUpFade 1s ease-out forwards" }}
        >
          +10
        </div>
      )}

      {/* Current question */}
      <QuizQuestion
        key={current}
        quiz={q}
        onAnswer={handleAnswer}
      />
    </div>
  );
};
